import React from 'react';
import { useTranslation } from 'react-i18next';

interface SocialLoginButtonProps {
  provider: 'google' | 'facebook' | 'twitter' | 'linkedin' | 'github';
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'button' | 'long-button';
  className?: string;
}

const socialProviders = {
  google: {
    name: 'Google',
    color: '#DB4437',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
    gradient: 'linear-gradient(90deg, rgb(79, 113, 232) 0%, rgb(79, 113, 232) 100%)',
    borderColor: 'rgba(79, 113, 232, 1)'
  },
  facebook: {
    name: 'Facebook',
    color: '#1877F2',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#fff" d="M22.688 0H1.323C.589 0 0 .589 0 1.322v21.356C0 23.41.59 24 1.323 24h11.505v-9.289H9.693V11.09h3.124V8.422c0-3.1 1.89-4.789 4.658-4.789 1.322 0 2.467.1 2.8.145v3.244h-1.922c-1.5 0-1.801.711-1.801 1.767V11.1h3.59l-.466 3.622h-3.113V24h6.114c.734 0 1.323-.589 1.323-1.322V1.322A1.302 1.302 0 0 0 22.688 0z"/>
      </svg>
    ),
    gradient: 'linear-gradient(90deg, #1877F2 0%, #1877F2 100%)',
    borderColor: '#1877F2'
  },
  twitter: {
    name: 'Twitter',
    color: '#1DA1F2',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#fff" d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
      </svg>
    ),
    gradient: 'linear-gradient(90deg, #1DA1F2 0%, #1DA1F2 100%)',
    borderColor: '#1DA1F2'
  },
  linkedin: {
    name: 'LinkedIn',
    color: '#0077B5',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#fff" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    gradient: 'linear-gradient(90deg, #0077B5 0%, #0077B5 100%)',
    borderColor: '#0077B5'
  },
  github: {
    name: 'GitHub',
    color: '#333',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#fff" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
      </svg>
    ),
    gradient: 'linear-gradient(90deg, #333 0%, #333 100%)',
    borderColor: '#333'
  }
};

export const SocialLoginButton: React.FC<SocialLoginButtonProps> = ({
  provider,
  onClick,
  loading = false,
  disabled = false,
  size = 'md',
  variant = 'button',
  className = ''
}) => {
  const { t } = useTranslation();
  const providerConfig = socialProviders[provider];

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center rounded-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses[size]} ${className}`}
        style={{
          backgroundColor: providerConfig.color,
          color: '#fff'
        }}
      >
        {loading ? (
          <svg className={`animate-spin ${iconSizeClasses[size]} text-white`} viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          providerConfig.icon
        )}
      </button>
    );
  }

  if (variant === 'long-button') {
    return (
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center rounded-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses[size]} ${className}`}
        style={{
          background: providerConfig.gradient,
          border: `1px solid ${providerConfig.borderColor}`,
          borderBottomWidth: 'thin'
        }}
      >
        {loading ? (
          <svg className={`animate-spin ${iconSizeClasses[size]} text-white mr-3`} viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <span className="mr-3">{providerConfig.icon}</span>
        )}
        <span style={{ color: '#ffffff', fontWeight: '500' }}>
          {loading ? t('auth.loggingIn', '로그인 중...') : t(`auth.${provider}Login`, `${providerConfig.name} 로그인`)}
        </span>
      </button>
    );
  }

  // Default button variant
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center border border-gray-300 rounded-lg font-medium bg-white hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses[size]} ${className}`}
    >
      {loading ? (
        <svg className={`animate-spin ${iconSizeClasses[size]} text-primary-600 mr-2`} viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <span className="mr-2">{providerConfig.icon}</span>
      )}
      {loading ? t('auth.loggingIn', '로그인 중...') : t(`auth.${provider}Login`, `${providerConfig.name} 로그인`)}
    </button>
  );
};

export default SocialLoginButton; 